
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import Layout from '../../../components/docs/Layout';
export const metadata = {
  toc: true,
  layout: 'guide',
  description: 'Input Validation Rules'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Callout = makeShortcode("Callout");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <Header title='Input validation rules' subtitle='' mdxType="Header" />
    <p>{`Plaid does as much as possible to ensure the user data you collect and verify is clean and standardized. If you are submitting data about the user via the API, you will need to match the formatting and standardization requirements described below.`}</p>
    <p>{`To help with development, we also provide a `}<a parentName="p" {...{
        "href": "/schema/identity_verification_api.json"
      }}>{`JSON file containing the same validation rules`}</a>{`.`}</p>
    <h4 {...{
      "id": "id-numbers"
    }}>{`ID numbers`}</h4>
    <p>{`Plaid's Data Source Verification will automatically collect, validate, and verify any of the following ID numbers for countries that you've enabled in the Dashboard Editor.`}</p>
    <p>{`If you are separately collecting ID numbers outside of Link, or if you have existing customer data you're looking to import into Plaid, you can submit this data programmatically via `}<inlineCode parentName="p">{`/identity_verification/create`}</inlineCode>{` or `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{`.`}</p>
    <p>{`The table below lists each ID number that Identity Verification supports. The `}<inlineCode parentName="p">{`API type`}</inlineCode>{` is a unique identifier that you should provide via the `}<inlineCode parentName="p">{`id_number.type`}</inlineCode>{` field.`}</p>
    <p>{`ID Numbers submitted via API should be stripped of any formatting characters like spaces, periods, and dashes. The table below also includes the exact regular expressions we use to decide whether a given ID Number is well-formed and valid for that region's ID type.`}</p>
    <p>{`Plaid may perform additional validation to ensure the ID number is valid for the selected type. For example, the SSA will not issue SSNs with specific numbers in certain positions. SSNs that meet any of the following conditions will not be accepted:`}</p>
    <ul>
      <li parentName="ul">{`Begin with "9", "666", or "000"`}</li>
      <li parentName="ul">{`Have the number "00" in positions 4 – 5`}</li>
      <li parentName="ul">{`Have the number "0000" in positions 6 – 9`}</li>
    </ul>
    <p>{`These conditions apply to Social Security Numbers. Because this field also accepts Individual Taxpayer Identification Numbers (ITINs), which begin with `}<inlineCode parentName="p">{`9`}</inlineCode>{`, a valid ITIN is accepted even though an SSN beginning with `}<inlineCode parentName="p">{`9`}</inlineCode>{` is not.`}</p>
    <p><em parentName="p">{`Need this data for your code? `}<a parentName="em" {...{
          "href": "/schema/identity_verification_api.json"
        }}>{`Download these validation rules in JSON form`}</a></em></p>
    {/* Note: this table is autogenerated by bin/id_number.md (docs/address_validation.md). Do not edit it directly. */}
    {/* Note: If you edit this table you should also edit the FlowIDNumberType and FlowIDNumberCategory enums in cognito-api.yaml */}
    <Layout.FullWidth>
      <table>
        <thead parentName="table">
          <tr parentName="thead">
            <th parentName="tr" {...{
              "align": null
            }}>{`Country`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Country Code`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`ID Name`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`API Type`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Format`}</th>
          </tr>
        </thead>
        <tbody parentName="table">
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Argentina`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`AR`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Documento Nacional de Identidad (DNI)`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`ar_dni`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`\\A\\d{8,10}\\z`}</inlineCode></td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Australia`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`AU`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Driver's License`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`au_drivers_license`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`\\A[A-Z0-9]{6,11}\\z`}</inlineCode></td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Australia`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`AU`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Passport Number`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`au_passport`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`\\A[A-Z0-9]{7,9}\\z`}</inlineCode></td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Brazil`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`BR`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Cadastro de Pessoas Físicas (CPF)`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`br_cpf`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`\\A\\d{11}\\z`}</inlineCode></td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Canada`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`CA`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Social Insurance Number (SIN)`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`ca_sin`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`\\A\\d{9}\\z`}</inlineCode></td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Chile`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`CL`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Rol Único Nacional (RUN)`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`cl_run`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`\\A\\d{7,8}[0-9Kk]\\z`}</inlineCode></td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`China`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`CN`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Resident Identity Card Number`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`cn_resident_card`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`\\A\\d{15}\\|\\d{17}(?:\\d\\|X)\\z`}</inlineCode></td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Colombia`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`CO`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Número de Identificación Tributaria (NIT)`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`co_nit`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`\\A\\d{8,10}\\z`}</inlineCode></td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Denmark`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`DK`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Det Centrale Personregister (CPR)`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`dk_cpr`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`\\A\\d{9,11}\\z`}</inlineCode></td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Egypt`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`EG`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`National ID Card Number`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`eg_national_id`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`\\A\\d{14}\\z`}</inlineCode></td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Hong Kong`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`HK`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Hong Kong Identity (HKID)`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`hk_hkid`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`\\A[A-Z0-9]{8,9}\\z`}</inlineCode></td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`India`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`IN`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Permanent Account Number (PAN)`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`in_pan`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`\\A[A-Z]{5}\\d{4}[A-Z]\\z`}</inlineCode></td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`India`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`IN`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Voter ID (EPIC)`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`in_epic`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`\\A[A-Z]{3}\\d{7}\\z`}</inlineCode></td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Italy`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`IT`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Codice Fiscale (CF)`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`it_cf`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`\\A[A-Z]{6}\\d{2}[A-Z]\\d{2}[A-Z]\\d{3}[A-Z]\\z`}</inlineCode></td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Japan`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`JP`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`"My Number" Resident Record Code`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`jp_my_number`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`\\A\\d{12}\\z`}</inlineCode></td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Jordan`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`JO`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Civil Identification Number`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`jo_civil_id`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`\\A\\d{10,14}\\z`}</inlineCode></td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Kenya`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`KE`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Huduma Namba`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`ke_huduma_namba`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`\\A\\d{10}\\z`}</inlineCode></td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Kuwait`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`KW`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Civil ID Card Number`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`kw_civil_id`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`\\A\\d{12}\\z`}</inlineCode></td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Malaysia`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`MY`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`National Registration Identity Card Number (NRIC)`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`my_nric`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`\\A\\d{12}\\z`}</inlineCode></td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Mexico`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`MX`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Clave Única de Registro de Población (CURP)`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`mx_curp`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`\\A[A-Z]{4}\\d{6}[A-Z]{6}[A-Z\\d]\\d\\z`}</inlineCode></td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Mexico`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`MX`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Registro Federal de Contribuyentes (RFC)`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`mx_rfc`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`\\A[A-Z]{4}\\d{6}[A-Z\\d]{3}\\z`}</inlineCode></td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`New Zealand`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`NZ`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Driver's License`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`nz_drivers_license`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`\\A[A-Z]{2}\\d{6}\\z`}</inlineCode></td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Nigeria`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`NG`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`National ID Number`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`ng_nin`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`\\A\\d{11}\\z`}</inlineCode></td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Oman`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`OM`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Identity Card Number`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`om_civil_id`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`\\A\\d{8,9}\\z`}</inlineCode></td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Philippines`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`PH`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`PhilSys Number`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`ph_psn`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`\\A\\d{12}\\z`}</inlineCode></td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Poland`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`PL`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`PESEL Number`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`pl_pesel`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`\\A\\d{11}\\z`}</inlineCode></td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Romania`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`RO`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Cod Numeric Personal (CNP)`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`ro_cnp`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`\\A\\d{13}\\z`}</inlineCode></td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Saudi Arabia`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`SA`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Biṭāgat Al-ʼaḥwāl`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`sa_national_id`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`\\A[A-Z0-9]{10}\\z`}</inlineCode></td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Singapore`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`SG`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`National Registration Identity Card`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`sg_nric`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`\\A[A-Z]\\d{7}[A-Z]\\z`}</inlineCode></td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`South Africa`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`ZA`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Smart ID Card Number`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`za_smart_id`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`\\A\\d{13}\\z`}</inlineCode></td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Spain`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`ES`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Documento Nacional de Identidad (DNI)`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`es_dni`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`\\A\\d{8}[A-Z]\\z`}</inlineCode></td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Spain`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`ES`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Número de Identidad de Extranjero (NIE)`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`es_nie`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`\\A[A-Z]\\d{7}[A-Z]\\z`}</inlineCode></td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Sweden`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`SE`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Personnummer (PIN)`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`se_pin`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`\\A\\d{9,12}\\z`}</inlineCode></td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Turkey`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`TR`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`T.C. Kimlik No.`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`tr_tc_kimlik`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`\\A\\d{11}\\z`}</inlineCode></td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`United States`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`US`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Social Security Number (SSN or ITIN)`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`us_ssn`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`\\A\\d{9}\\z`}</inlineCode></td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`United States`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`US`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Social Security Number (SSN or ITIN) Last 4`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`us_ssn_last_4`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`\\A\\d{4}\\z`}</inlineCode></td>
          </tr>
        </tbody>
      </table>
    </Layout.FullWidth>
    <h4 {...{
      "id": "input-validation-by-country"
    }}>{`Input validation by country`}</h4>
    <p>{`Address standards vary by country. Some countries do not support postal code or region (the equivalent of "state" or "province" depending on the region). In other countries, postal code and/or region is valid in a sense, but superfluous and not typically collected in native forms.`}</p>
    <p>{`Separately, variations in identity data coverage mean that collection of "ID Number" is a required part of Data Source Verification in some countries, optional (configured in the Dashboard Editor) for other countries, and not supported in countries that do not support Data Source Verification at all.`}</p>
    <p>{`This regional variation is reflected in the table below.`}</p>
    <p>{`The labels for each input field below correspond to the following input validations:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li"><inlineCode parentName="p">{`address.postal_code`}</inlineCode>{` and `}<inlineCode parentName="p">{`address.region`}</inlineCode></p>
        <ul parentName="li">
          <li parentName="ul">
            <p parentName="li"><strong parentName="p">{`Required`}</strong>{` - If `}<inlineCode parentName="p">{`address`}</inlineCode>{` is provided as part of the request, this field must always be present. For a set of accepted `}<inlineCode parentName="p">{`region`}</inlineCode>{` codes, see `}<a parentName="p" {...{
                "href": "https://plaid.com/documents/country_subdivision_codes.json"
              }}>{`country subdivision codes in JSON form`}</a>{`. If a country is absent from this list, it means that `}<inlineCode parentName="p">{`address.region`}</inlineCode>{` is not accepted for that country.`}</p>
          </li>
          <li parentName="ul">
            <p parentName="li"><strong parentName="p">{`Not Accepted`}</strong>{` - If `}<inlineCode parentName="p">{`address`}</inlineCode>{` is provided as part of the request, this field must be omitted. Specifically, the key should be omitted from the `}<inlineCode parentName="p">{`address`}</inlineCode>{` object (as opposed to the key being present with a null value)`}</p>
          </li>
        </ul>
      </li>
    </ul>
    <Callout mdxType="Callout">
      <p>{`For the `}<inlineCode parentName="p">{`region`}</inlineCode>{` field, Identity Verification accepts a subset of ISO 3166-2 subdivision codes, excluding subdivision codes that are not typically used in addresses (these exclusions primarily impact Italy and Hong Kong). For a full list of accepted codes, see `}<a parentName="p" {...{
          "href": "https://plaid.com/documents/country_subdivision_codes.json"
        }}>{`country subdivision codes in JSON form`}</a>{`. Only the portion of the region code after the hyphen should be sent, since the country code can be extrapolated from the `}<inlineCode parentName="p">{`country`}</inlineCode>{` field. For example, for Rome, use `}<inlineCode parentName="p">{`country: "IT", region: "RM"`}</inlineCode>{`.`}</p>
    </Callout>
    <ul>
      <li parentName="ul">
        <p parentName="li"><inlineCode parentName="p">{`id_number`}</inlineCode></p>
        <ul parentName="li">
          <li parentName="ul">
            <p parentName="li"><strong parentName="p">{`Required`}</strong>{` - If `}<inlineCode parentName="p">{`id_number`}</inlineCode>{` is not provided for this country, `}<inlineCode parentName="p">{`kyc_check`}</inlineCode>{` will treat the Identity Verification Session as having incomplete data and will require user interaction with the Identity Verification modal in order to collect `}<inlineCode parentName="p">{`id_number`}</inlineCode></p>
          </li>
          <li parentName="ul">
            <p parentName="li"><strong parentName="p">{`Optional`}</strong>{` - If `}<inlineCode parentName="p">{`id_number`}</inlineCode>{` is not provided for this country `}<strong parentName="p">{`and`}</strong>{` your Identity Verification Template is configured to not collect ID numbers for this country then Identity Verification will automatically run the `}<inlineCode parentName="p">{`kyc_check`}</inlineCode>{` step without any user interaction as long as all other required input fields are provided. Similar to the address fields, the API requires you omit the `}<inlineCode parentName="p">{`id_number`}</inlineCode>{` key entirely if you are not providing ID number data`}</p>
          </li>
          <li parentName="ul">
            <p parentName="li"><strong parentName="p">{`Not Accepted`}</strong>{` - ID number verification is not supported for this country and the `}<inlineCode parentName="p">{`id_number`}</inlineCode>{` field should always be omitted from API requests`}</p>
          </li>
        </ul>
      </li>
    </ul>
    <p><em parentName="p">{`Need this data for your code? `}<a parentName="em" {...{
          "href": "/schema/identity_verification_api.json"
        }}>{`Download these validation rules in JSON form`}</a></em></p>
    {/* Note: this table is autogenerated by bin/generate-docs (docs/address_validation.md). Do not edit it directly. */}
    <Layout.FullWidth>
      <table>
        <thead parentName="table">
          <tr parentName="thead">
            <th parentName="tr" {...{
              "align": null
            }}>{`Country`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Country Code`}</th>
            <th parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="th">{`address.postal_code`}</inlineCode></th>
            <th parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="th">{`address.region`}</inlineCode></th>
            <th parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="th">{`id_number`}</inlineCode></th>
          </tr>
        </thead>
        <tbody parentName="table">
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Afghanistan`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`AF`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Albania`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`AL`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Algeria`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`DZ`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Andorra`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`AD`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Angola`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`AO`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Anguilla`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`AI`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Antigua & Barbuda`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`AG`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Argentina`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`AR`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Armenia`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`AM`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Aruba`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`AW`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Australia`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`AU`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Austria`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`AT`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Azerbaijan`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`AZ`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Bahamas`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`BS`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Bahrain`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`BH`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Bangladesh`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`BD`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Barbados`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`BB`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Belarus`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`BY`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Belgium`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`BE`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Belize`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`BZ`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Benin`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`BJ`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Bermuda`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`BM`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Bhutan`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`BT`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Bolivia`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`BO`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Bosnia & Herzegovina`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`BA`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Botswana`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`BW`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Brazil`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`BR`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`British Virgin Islands`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`VG`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Brunei`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`BN`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Bulgaria`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`BG`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Burkina Faso`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`BF`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Burundi`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`BI`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Cambodia`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`KH`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Cameroon`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`CM`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Canada`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`CA`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Optional`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Cape Verde`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`CV`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Caribbean Netherlands`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`BQ`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Cayman Islands`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`KY`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Central African Republic`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`CF`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Chad`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`TD`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Chile`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`CL`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`China`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`CN`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Colombia`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`CO`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Comoros`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`KM`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Congo - Brazzaville`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`CG`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Cook Islands`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`CK`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Costa Rica`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`CR`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Croatia`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`HR`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Curaçao`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`CW`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Cyprus`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`CY`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Czech Republic`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`CZ`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Côte d'Ivoire`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`CI`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Denmark`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`DK`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Djibouti`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`DJ`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Dominica`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`DM`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Dominican Republic`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`DO`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Ecuador`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`EC`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Egypt`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`EG`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`El Salvador`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`SV`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Eritrea`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`ER`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Estonia`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`EE`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Eswatini`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`SZ`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Ethiopia`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`ET`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Faroe Islands`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`FO`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Fiji`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`FJ`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Finland`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`FI`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`France`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`FR`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`French Polynesia`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`PF`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Gabon`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`GA`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Gambia`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`GM`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Georgia`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`GE`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Germany`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`DE`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Ghana`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`GH`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Gibraltar`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`GI`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Greece`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`GR`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Greenland`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`GL`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Grenada`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`GD`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Guatemala`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`GT`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Guernsey`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`GG`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Guinea`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`GN`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Guinea-Bissau`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`GW`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Guyana`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`GY`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Haiti`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`HT`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Honduras`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`HN`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Hong Kong`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`HK`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Hungary`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`HU`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Iceland`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`IS`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`India`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`IN`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Optional`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Indonesia`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`ID`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Iraq`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`IQ`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Ireland`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`IE`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Isle of Man`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`IM`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Israel`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`IL`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Italy`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`IT`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Optional`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Jamaica`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`JM`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Japan`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`JP`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Optional`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Jersey`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`JE`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Jordan`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`JO`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Kazakhstan`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`KZ`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Kenya`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`KE`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Kiribati`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`KI`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Kosovo`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`XK`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Kuwait`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`KW`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Kyrgyzstan`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`KG`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Laos`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`LA`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Latvia`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`LV`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Lebanon`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`LB`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Lesotho`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`LS`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Liberia`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`LR`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Liechtenstein`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`LI`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Lithuania`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`LT`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Luxembourg`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`LU`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Macao SAR China`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`MO`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Madagascar`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`MG`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Malawi`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`MW`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Malaysia`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`MY`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Maldives`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`MV`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Mali`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`ML`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Malta`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`MT`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Mauritania`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`MR`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Mauritius`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`MU`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Mexico`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`MX`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Optional`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Moldova`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`MD`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Monaco`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`MC`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Mongolia`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`MN`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Montenegro`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`ME`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Montserrat`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`MS`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Morocco`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`MA`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Mozambique`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`MZ`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Myanmar (Burma)`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`MM`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Namibia`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`NA`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Nauru`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`NR`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Nepal`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`NP`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Netherlands`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`NL`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`New Zealand`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`NZ`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Optional`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Nicaragua`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`NI`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Niger`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`NE`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Nigeria`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`NG`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Niue`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`NU`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`North Macedonia`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`MK`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Norway`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`NO`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Oman`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`OM`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Pakistan`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`PK`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Palau`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`PW`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Palestinian Territories`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`PS`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Panama`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`PA`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Papua New Guinea`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`PG`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Paraguay`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`PY`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Peru`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`PE`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Philippines`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`PH`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Optional`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Poland`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`PL`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Optional`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Portugal`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`PT`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Qatar`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`QA`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Romania`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`RO`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Russia`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`RU`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Rwanda`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`RW`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Samoa`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`WS`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`San Marino`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`SM`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Saudi Arabia`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`SA`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Senegal`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`SN`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Serbia`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`RS`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Seychelles`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`SC`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Sierra Leone`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`SL`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Singapore`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`SG`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Optional`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Slovakia`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`SK`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Slovenia`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`SI`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Solomon Islands`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`SB`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Somalia`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`SO`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`South Africa`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`ZA`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`South Korea`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`KR`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`South Sudan`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`SS`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Spain`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`ES`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Optional`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Sri Lanka`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`LK`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`St. Kitts & Nevis`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`KN`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`St. Lucia`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`LC`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`St. Martin`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`MF`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`St. Vincent & Grenadines`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`VC`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Sudan`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`SD`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Suriname`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`SR`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Sweden`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`SE`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Switzerland`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`CH`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`São Tomé & Príncipe`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`ST`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Taiwan`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`TW`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Tajikistan`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`TJ`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Tanzania`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`TZ`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Thailand`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`TH`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Timor-Leste`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`TL`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Togo`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`TG`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Tonga`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`TO`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Trinidad & Tobago`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`TT`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Tunisia`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`TN`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Turkey`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`TR`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Turkmenistan`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`TM`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Turks & Caicos Islands`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`TC`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Tuvalu`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`TV`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Uganda`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`UG`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Ukraine`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`UA`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`United Arab Emirates`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`AE`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`United Kingdom`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`GB`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`United States`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`US`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Optional`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Uruguay`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`UY`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Uzbekistan`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`UZ`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Vanuatu`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`VU`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Vatican City`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`VA`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Venezuela`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`VE`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Vietnam`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`VN`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Western Sahara`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`EH`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Yemen`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`YE`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Zambia`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`ZM`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Required`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Zimbabwe`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`ZW`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Not Accepted`}</td>
          </tr>
        </tbody>
      </table>
    </Layout.FullWidth>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "id-numbers",
        level: 4,
        title: "ID numbers",
      },
{
        id: "input-validation-by-country",
        level: 4,
        title: "Input validation by country",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
